<script setup lang="ts">
/* Components */
import TheIcon from '@/components/UI/TheIcon/TheIcon.vue'

const TYPES: Record<Type, Type> = {
  selection: 'selection',
  simple: 'simple',
  extras: 'extras',
  gift: 'gift',
}

type Type = 'selection' | 'simple' | 'extras' | 'gift'

type Props = {
  type: Type
  price?: number | string
  oldPrice?: number | string
  currency?: string
  isActive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currency: '₽',
  price: 0,
  oldPrice: 0,
})

const emit = defineEmits(['onAdd', 'onIncrease', 'onDecrease'])

const { isDesktop } = useDevice()

const cardButtonClasses = computed(() => {
  const classes = ['card-button', `card-button--type-${props.type}`]

  if (props.isActive) classes.push('card-button--active')

  return classes
})

const pricePrefix = computed(() => {
  if (props.type === TYPES.selection) return 'от '

  return ''
})

const priceValue = computed(() => {
  return `${pricePrefix.value}${props.price} ${props.currency}`
})

const oldPriceValue = computed(() => {
  return `${props.oldPrice} ${props.currency}`
})

const appendIconName = computed(() => {
  if (props.type === TYPES.gift && props.isActive) return 'tick'

  if (props.type === TYPES.selection && props.isActive) return 'plus'

  if ([TYPES.extras, TYPES.simple, TYPES.gift].includes(props.type)) {
    return 'plus'
  }

  return 'arrow-right'
})

const isGiftType = computed(() => props.type === TYPES.gift)

const showRemoveButton = computed(() => {
  return props.isActive && !isGiftType.value
})

const showAddButton = computed(() => {
  return props.isActive || isGiftType.value
})

const onClickRoot = () => {
  if (!props.isActive) emit('onAdd')
}
</script>

<template>
  <div
    v-ripple="isDesktop"
    :class="cardButtonClasses"
    @click.self="onClickRoot"
  >
    <v-btn
      v-if="showRemoveButton"
      class="card-button__button"
      :class="{ 'card-button__button--disabled': !isActive }"
      icon
      @click="emit('onDecrease')"
    >
      <the-icon name="minus" />
    </v-btn>

    <div v-if="!isGiftType" class="card-button__wrapper">
      <div v-if="oldPrice && !isActive" class="card-button__old-price">
        {{ oldPriceValue }}
      </div>

      <div class="card-button__price">{{ priceValue }}</div>

      <the-icon
        v-show="!isActive"
        class="card-button__icon"
        :name="appendIconName"
      />
    </div>

    <v-btn
      v-if="showAddButton"
      :ripple="true"
      class="card-button__button"
      :class="{ 'card-button__button--disabled': !isActive }"
      icon
      @click="emit('onIncrease')"
    >
      <the-icon :name="appendIconName" />
    </v-btn>
  </div>
</template>

<style lang="scss" scoped>
.card-button {
  --gap: 8px;
  --button-width: 40px;
  --button-height: 40px;

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: var(--ui-primary);
  letter-spacing: -0.02em;
  cursor: pointer;
  background-color: var(--ui-white);
  border-radius: 100px;
  box-shadow: var(--ui-card-shadow);
  transition: background-color 0.3s ease-in-out, box-shadow 0.2s ease-in-out;

  @include media('>=desktop-mini') {
    --button-width: 72px;
  }

  &:active {
    color: var(--ui-white);
    background-color: var(--ui-primary);
  }

  :deep(svg) {
    fill: currentColor !important;
    stroke: currentColor !important;
  }

  :deep(.v-ripple__animation) {
    background-color: var(--ui-primary);
  }

  :deep(.v-ripple__animation--in) {
    opacity: 1;
  }

  &__wrapper {
    position: relative;
    z-index: 1;
    display: flex;
    gap: var(--gap);
    align-items: center;
    pointer-events: none;
    user-select: none;
  }

  &__button {
    position: relative;
    z-index: 1;
    width: var(--button-width) !important;
    height: var(--button-height) !important;
    color: inherit;
    background-color: transparent;
    border-radius: inherit;
    box-shadow: none !important;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

    :deep(.v-ripple__animation) {
      background-color: currentColor;
    }

    :deep(.v-ripple__animation--in) {
      opacity: 0.25;
    }

    &--disabled {
      pointer-events: none;
    }

    :deep(.v-btn__overlay) {
      display: none;
    }
  }

  &__price {
    font-size: 12px;
    font-weight: 800;
    line-height: 16px;
    white-space: nowrap;
    transition: color 0.2s ease-in-out;

    @include media('>=desktop-mini') {
      font-size: 16px;
      line-height: 20px;
    }
  }

  &__old-price {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-dark-grey);
    text-decoration: line-through;
    white-space: nowrap;

    @include media('>=desktop-mini') {
      font-size: 14px;
      line-height: 18px;
    }
  }
}

/* Modifiers */
.card-button--active {
  color: var(--ui-white);
  cursor: auto;
  background-color: var(--ui-primary);
  box-shadow: none;

  .card-button__wrapper {
    margin: 0 auto;
  }
}

.card-button--type-selection {
  height: 40px;
}

.card-button--type-simple {
  height: 40px;
}

.card-button--type-gift {
  --button-width: 100%;
  --button-height: 40px;

  height: 40px;
}

.card-button--type-extras {
  --gap: 4px;
  --button-width: 32px;
  --button-height: 32px;

  height: 32px;

  @include media('>=desktop-mini') {
    --gap: 8px;
    --button-width: 40px;
    --button-height: 40px;

    height: 40px;
  }
}
</style>
